/**
 * usePauseSubscriptionFlow — la machine de « Mettre en pause l'abonnement » → les props de
 * `<PauseSubscriptionFlow>`.
 *
 * Deux appels, ceux de `ParentPauseSubscriptionModal.js` et rien d'autre :
 *   - `getParentPauseBalance` à l'ouverture (`:143`, effet sur `visible`) ;
 *   - `pauseSubscriptionParent` à la confirmation (`:242-254`), corps
 *     `{ start_date, resume_date, reason, tutor_loss_acknowledged }`.
 *
 * Les règles chiffrées (quota, durée minimale, durée maximale consécutive, seuil de perte de
 * créneau, frontières d'été) vivent dans `adapters/parent/SubscriptionPause/` : ici il ne reste
 * que la machine et l'aller-retour réseau.
 *
 * Trois guard refs, comme toute mutation des ports (`ports/CLAUDE.md` § write-side). Il n'y a pas
 * de `pendingId` à comparer : le parcours est unique à l'écran et le service part sans paramètre
 * de route — la troisième garde est donc celle du succès, une réaction par issue.
 *
 * **Un seul parcours tant qu'un envoi est en vol**, comme le rattrapage (`useRecoverAbsenceFlow`).
 * Fermer pendant l'envoi ne fait que CACHER la feuille : la pause est partie, sa réponse doit
 * encore être lue (accusé, relecture du portail par `onSuccess`). La rouvrir avant la réponse la
 * remontre telle quelle, bouton en chargement — une feuille neuve laisserait partir une seconde
 * pause. À la réponse, feuille visible : l'accusé ou le refus s'écrivent dans l'étape ; feuille
 * cachée : le bandeau du portail le dit (`notice`). Un succès remet alors le parcours à zéro ; un
 * refus le garde, pour que la feuille rouverte le montre dans son étape, dates intactes. La
 * fermeture n'est jamais verrouillée : une requête sans réponse laisserait une feuille infermable.
 *
 * ⚠️ `student` est l'élève affiché (`selectDisplayedStudent`), et non le `selectedUser` du portail : `connect`
 * écrasait déjà la prop `user` de la modale legacy (`:730`), donc c'est ce compte-là que les
 * règles lisaient. En mode parent, c'est bien l'enfant affiché (la bascule change le compte
 * connecté). Voir `TRANSITION.md` § Pause.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../data/redux/actions/api"
import { subscriptionService } from "../../../../data/services/subscriptionService"
import { type SnackbarProps, type SnackbarTone } from "../../../../design-system/molecules/Snackbar"
import {
    type PauseSubscriptionFlowProps,
    type PauseSubscriptionStep,
} from "../../../../design-system/templates/PauseSubscriptionFlow/PauseSubscriptionFlow"
import {
    PAUSE_REASON_OPTIONS,
    pauseDayLabel,
    pauseDurationDays,
    pauseRequestBody,
    toPauseRules,
    toUIPauseRecap,
    type ApiParentPauseBalance,
} from "../../adapters/parent/SubscriptionPause/pauseBalanceAdapter"
import { pauseDateError, pauseDateWindow, pauseLimitNote } from "../../adapters/parent/SubscriptionPause/pauseDateRules"
import { childDisplayName } from "../formatters/childFormatter"
import { apiErrorMessage } from "./apiErrorMessage"
import { useGesture } from "./gestureTracking"
import { selectDisplayedStudent } from "../../view-models/shared/displayedStudent"

/**
 * Le seuil, tel que la case à cocher du legacy l'écrit (`:344-352`) — « 1 mois », alors que
 * `keep_lives_max_days` vaut 28 jours. Copie reprise telle quelle : l'écart est un sujet produit,
 * il existe aussi côté backend, et il ne se corrige pas dans un portage (`DECISIONS.md`).
 */
export const PAUSE_ACK_THRESHOLD_LABEL = "1 mois"

/**
 * Le repli du legacy quand le serveur refuse sans dire pourquoi (`:39-41`). Jumeau de
 * `PAUSE_SUBMIT_ERROR_FALLBACK`, que le design system déclare pour ses stories : la phrase n'est
 * pas importée de là, parce qu'une VALEUR du design system embarque Unistyles, que Jest ne sait
 * pas charger — les autres hooks des ports déclarent la leur pour la même raison.
 */
const PAUSE_SUBMIT_ERROR_FALLBACK = "Nous n'avons pas pu programmer la pause. Réessayez dans un instant."
/** Ce que le bandeau dit quand la feuille était cachée à la réponse — le titre de l'accusé. */
const NOTICE_PROGRAMMED = "Pause programmée"

export type PauseFlowState = {
    visible: boolean
    step: PauseSubscriptionStep
    /** `YYYY-MM-DD`. */
    startDate: string | null
    resumeDate: string | null
    reason: string | null
    ackAccepted: boolean
    /** Vrai de l'envoi à sa réponse : fermer ne fait alors que cacher la feuille. */
    submitting: boolean
    errorMessage: string | null
    /** Le bandeau du portail — l'issue d'un envoi dont la feuille était cachée. */
    notice: { message: string; tone: SnackbarTone } | null
    /** L'élève du parcours, pris à l'ouverture : un refus gardé ne se remontre qu'à lui. */
    studentId: string | number | null
    /** Le parent arrive du parcours de résiliation : la pause y va jusqu'à 1 mois. */
    fromCancellation: boolean
}

export const initialPauseFlowState: PauseFlowState = {
    visible: false,
    step: "dates",
    startDate: null,
    resumeDate: null,
    reason: null,
    ackAccepted: false,
    submitting: false,
    errorMessage: null,
    notice: null,
    studentId: null,
    fromCancellation: false,
}

export type PauseFlowAction =
    | { type: "OPEN"; studentId?: string | number | null; fromCancellation?: boolean }
    | { type: "CLOSE" }
    | { type: "SET_START_DATE"; date: string }
    | { type: "SET_RESUME_DATE"; date: string }
    | { type: "SELECT_REASON"; reason: string }
    | { type: "SET_ACK"; accepted: boolean }
    | { type: "CONTINUE" }
    | { type: "BACK" }
    | { type: "SUBMIT" }
    | { type: "SUBMIT_SUCCESS" }
    | { type: "SUBMIT_ERROR"; message: string }
    | { type: "DISMISS_NOTICE" }

/** Un parcours remis à zéro : seul le bandeau survit, il parle d'un envoi déjà joué. */
function resetFlow(state: PauseFlowState): PauseFlowState {
    return { ...initialPauseFlowState, notice: state.notice }
}

export function pauseFlowReducer(state: PauseFlowState, action: PauseFlowAction): PauseFlowState {
    switch (action.type) {
        case "OPEN": {
            const studentId = action.studentId ?? null
            const fromCancellation = action.fromCancellation ?? false
            // Un envoi en vol : c'est SON parcours qu'on remontre, tel quel. Un refus arrivé feuille
            // cachée aussi — il se lit dans son étape —, tant que c'est le même élève qui rouvre.
            // Rouverte depuis l'autre entrée (portail ↔ résiliation), la feuille n'a plus la même limite :
            // le refus gardé ne vaut plus.
            const refusalKept =
                !state.visible &&
                state.errorMessage != null &&
                state.studentId === studentId &&
                state.fromCancellation === fromCancellation
            if (state.submitting || refusalKept) return { ...state, visible: true }
            // Le legacy vide tout à chaque ouverture (`:139-146`) : une pause abandonnée ne
            // revient pas à moitié remplie.
            return { ...resetFlow(state), visible: true, studentId, fromCancellation }
        }

        case "CLOSE":
            // Pendant l'envoi, la feuille se cache seulement : la réponse doit retrouver son parcours.
            if (state.submitting) return { ...state, visible: false }
            return resetFlow(state)

        case "SET_START_DATE":
            return {
                ...state,
                startDate: action.date,
                // Une reprise antérieure ou égale au nouveau début n'a plus de sens (`:750-757`).
                resumeDate:
                    state.resumeDate != null && moment(state.resumeDate).isSameOrBefore(moment(action.date))
                        ? null
                        : state.resumeDate,
            }

        case "SET_RESUME_DATE":
            return { ...state, resumeDate: action.date }

        case "SELECT_REASON":
            return { ...state, reason: action.reason }

        case "SET_ACK":
            return { ...state, ackAccepted: action.accepted }

        case "CONTINUE":
            return state.step === "dates" ? { ...state, step: "recap" } : state

        case "BACK":
            // Le refus du serveur ne survit pas au retour : il portait les dates d'avant.
            return state.step === "recap" ? { ...state, step: "dates", errorMessage: null } : state

        case "SUBMIT":
            return { ...state, submitting: true, errorMessage: null }

        case "SUBMIT_SUCCESS":
            if (!state.visible) {
                return { ...resetFlow(state), notice: { message: NOTICE_PROGRAMMED, tone: "success" } }
            }
            return { ...state, submitting: false, errorMessage: null, step: "done" }

        case "SUBMIT_ERROR": {
            // Feuille visible ou cachée, le refus reste dans son étape avec ses dates : rien n'est
            // perdu. Cachée, le bandeau le dit en plus — sinon le parent croirait sa pause programmée.
            const refused = { ...state, submitting: false, errorMessage: action.message }
            return state.visible ? refused : { ...refused, notice: { message: action.message, tone: "danger" } }
        }

        case "DISMISS_NOTICE":
            return state.notice == null ? state : { ...state, notice: null }

        default:
            return state
    }
}

type ApiSlice = { request?: boolean; success?: boolean; error?: unknown }

export type PauseSubscriptionFlowConfig = {
    /** Prénom de l'enfant — l'écran le connaît déjà, il évite une seconde lecture du store. */
    childFirstname?: string | null
    /** Rejoué au succès : le portail recharge le solde de pause et le tarif. */
    onSuccess?: () => void
    /** Ouvre l'aide — le chemin d'une pause plus longue ou plus tôt que l'app ne le permet. */
    onContactSupport?: () => void
}

export type PauseFlowOpenOptions = {
    /** Le parent arrive du parcours de résiliation (« Faire une pause longue »). */
    fromCancellation?: boolean
}

export type PauseSubscriptionFlowAdapter = {
    /** Ouvre la feuille et redemande le solde, comme le legacy le fait sur `visible`. */
    open: (options?: PauseFlowOpenOptions) => void
    /** Les props de `<PauseSubscriptionFlow>`, prêtes à être étalées. */
    flowProps: PauseSubscriptionFlowProps
    /** Le bandeau du portail : l'issue d'un envoi dont la feuille était cachée à la réponse. */
    notice: SnackbarProps
}

export function usePauseSubscriptionFlow(config: PauseSubscriptionFlowConfig = {}): PauseSubscriptionFlowAdapter {
    const { childFirstname, onSuccess, onContactSupport } = config
    const dispatch = useDispatch()
    const [state, dispatchUi] = useReducer(pauseFlowReducer, initialPauseFlowState)
    // Stable : le bandeau réarme son minuteur de retrait à chaque nouveau `onDismiss`.
    const dismissNotice = useCallback(() => dispatchUi({ type: "DISMISS_NOTICE" }), [])

    const balanceApi = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => s?.data?.formData?.pauseBalance as ApiParentPauseBalance | undefined
    )
    const student = useSelector(selectDisplayedStudent)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const summerConfig = useSelector((s: any) => s?.data?.staticData?.summer_config)
    const submitReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => s?.api?.pauseSubscriptionParent as ApiSlice | undefined
    )

    const gesture = useGesture("subscription.pause")
    const awaitingSubmitRef = useRef(false)
    const handledSubmitSuccessRef = useRef(false)
    const handledSubmitErrorRef = useRef(false)

    const studentId = (student?.id ?? null) as string | number | null

    const open = useCallback(
        (options?: PauseFlowOpenOptions) => {
            gesture.opened()
            dispatchUi({ type: "OPEN", studentId, fromCancellation: options?.fromCancellation })
            // Un envoi en vol : ni ses gardes ni sa tranche ne sont touchées, sa réponse reste à lire.
            if (awaitingSubmitRef.current) return
            handledSubmitSuccessRef.current = false
            handledSubmitErrorRef.current = false
            // Un refus laissé sur la tranche repeindrait le récapitulatif à la réouverture (`:144`).
            dispatch(apiIdle(subscriptionService.pauseSubscriptionParent) as never)
            dispatch(apiRequest(subscriptionService.getParentPauseBalance) as never)
        },
        [dispatch, studentId, gesture]
    )

    // Fermer n'abandonne pas l'écoute : `awaitingSubmitRef` reste levé jusqu'à la réponse.
    const close = useCallback(() => dispatchUi({ type: "CLOSE" }), [])

    const rules = useMemo(() => toPauseRules(balanceApi, state.fromCancellation), [balanceApi, state.fromCancellation])
    const durationDays = pauseDurationDays(state.startDate, state.resumeDate)
    const limitNote = useMemo(() => pauseLimitNote({ now: moment(), rules, durationDays }), [rules, durationDays])

    const dateWindow = useMemo(
        () =>
            pauseDateWindow({
                now: moment(),
                summerConfig,
                hasSummerFastTrack: !!student?.has_summer_fast_track,
                rules,
                startDate: state.startDate,
            }),
        [summerConfig, student?.has_summer_fast_track, rules, state.startDate]
    )

    const submit = useCallback(() => {
        if (state.startDate == null || state.resumeDate == null || state.reason == null) return
        // Une pause en vol ne repart pas : le ref tranche aussi le double appui, que `submitting`
        // ne voit qu'au rendu suivant.
        if (awaitingSubmitRef.current) return
        awaitingSubmitRef.current = true
        handledSubmitSuccessRef.current = false
        handledSubmitErrorRef.current = false
        dispatchUi({ type: "SUBMIT" })
        dispatch(
            apiRequest(
                subscriptionService.pauseSubscriptionParent,
                {},
                pauseRequestBody({
                    startDate: state.startDate,
                    resumeDate: state.resumeDate,
                    reason: state.reason,
                    tutorLossAcknowledged: state.ackAccepted,
                    fromCancellation: state.fromCancellation,
                })
            ) as never
        )
        gesture.submitted()
    }, [dispatch, state.ackAccepted, state.fromCancellation, state.reason, state.resumeDate, state.startDate, gesture])

    // La feuille se ferme d'abord : l'aide s'ouvre par-dessus le portail, pas sous la feuille.
    const contactSupport = useMemo(
        () =>
            onContactSupport
                ? () => {
                      close()
                      onContactSupport()
                  }
                : undefined,
        [close, onContactSupport]
    )

    useEffect(() => {
        if (!awaitingSubmitRef.current || !submitReq?.success || handledSubmitSuccessRef.current) return
        handledSubmitSuccessRef.current = true
        awaitingSubmitRef.current = false
        gesture.succeeded()
        dispatchUi({ type: "SUBMIT_SUCCESS" })
        dispatch(apiIdle(subscriptionService.pauseSubscriptionParent) as never)
        // Le réducteur `formData` vide `pauseBalance` au succès : sans ce rechargement, la ligne
        // « Annuler la pause » du portail ne réapparaîtrait qu'au prochain montage de l'écran.
        onSuccess?.()
    }, [dispatch, onSuccess, submitReq?.success, gesture])

    useEffect(() => {
        if (!awaitingSubmitRef.current || !submitReq?.error || handledSubmitErrorRef.current) return
        handledSubmitErrorRef.current = true
        awaitingSubmitRef.current = false
        gesture.failed(submitReq.error)
        // La feuille reste ouverte : dates et motif ne sont pas perdus (`:216-227`).
        dispatchUi({ type: "SUBMIT_ERROR", message: apiErrorMessage(submitReq.error, PAUSE_SUBMIT_ERROR_FALLBACK) })
        dispatch(apiIdle(subscriptionService.pauseSubscriptionParent) as never)
    }, [dispatch, submitReq?.error, gesture])

    useEffect(
        () => () => {
            dispatch(apiIdle(subscriptionService.pauseSubscriptionParent) as never)
        },
        [dispatch]
    )

    const name = childDisplayName(childFirstname, student)

    // Feuille fermée : `<Overlay>` ne rend rien, donc règles de dates et récapitulatif
    // seraient composés pour personne — à chaque action du store, puisque l'écran du portail
    // garde la feuille montée. On rend les seules props qui décrivent une feuille fermée.
    const notice: SnackbarProps = {
        visible: state.notice != null,
        message: state.notice?.message ?? "",
        tone: state.notice?.tone ?? "success",
        onDismiss: dismissNotice,
    }

    if (!state.visible) {
        return {
            open,
            notice,
            flowProps: {
                visible: false,
                onClose: close,
                step: state.step,
                childFirstname: name,
                startDate: state.startDate,
                onChangeStartDate: (date: string) => dispatchUi({ type: "SET_START_DATE", date }),
                resumeDate: state.resumeDate,
                onChangeResumeDate: (date: string) => dispatchUi({ type: "SET_RESUME_DATE", date }),
                reasons: PAUSE_REASON_OPTIONS,
                onSelectReason: (reason: string) => dispatchUi({ type: "SELECT_REASON", reason }),
            },
        }
    }

    const dateError = pauseDateError({
        summerConfig,
        rules,
        startDate: state.startDate,
        resumeDate: state.resumeDate,
        durationDays,
        upcomingActivation: student?.upcoming_activation,
    })

    // Le legacy ne montre le bloc de conséquences que sur une fenêtre valide (`showRecap`, `:281-287`).
    const recapVisible =
        state.startDate != null && state.resumeDate != null && state.reason != null && dateError == null

    return {
        open,
        notice,
        flowProps: {
            visible: true,
            onClose: close,
            step: state.step,
            childFirstname: name,

            // Le solde de pause ne s'affiche pas (Wajih, 30/09) : il borne les calendriers et
            // nourrit le refus (`pauseDateWindow`, `pauseDateError`), rien de plus.
            startDate: state.startDate,
            onChangeStartDate: (date) => dispatchUi({ type: "SET_START_DATE", date }),
            startMinDate: dateWindow.startMinDate,
            startMaxDate: dateWindow.startMaxDate,
            startDateDisabled: dateWindow.startDateDisabled,
            limitNote,
            onContactSupport: contactSupport,

            resumeDate: state.resumeDate,
            onChangeResumeDate: (date) => dispatchUi({ type: "SET_RESUME_DATE", date }),
            resumeMinDate: dateWindow.resumeMinDate,
            resumeMaxDate: dateWindow.resumeMaxDate,

            durationLabel: state.startDate != null && state.resumeDate != null ? pauseDayLabel(durationDays) : null,
            dateError,

            reasons: PAUSE_REASON_OPTIONS,
            selectedReason: state.reason,
            onSelectReason: (reason) => dispatchUi({ type: "SELECT_REASON", reason }),

            onContinue: () => dispatchUi({ type: "CONTINUE" }),

            recap: recapVisible
                ? toUIPauseRecap({ durationDays, commitmentEndsAt: student?.commitment_ends_at })
                : null,

            ackRequired: durationDays > rules.keepLivesMaxDays,
            ackThresholdLabel: PAUSE_ACK_THRESHOLD_LABEL,
            ackAccepted: state.ackAccepted,
            onChangeAck: (accepted) => dispatchUi({ type: "SET_ACK", accepted }),

            onSubmit: submit,
            submitting: state.submitting,
            errorMessage: state.errorMessage,

            onBack: () => dispatchUi({ type: "BACK" }),
        },
    }
}
